import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Icon } from "../app/Icon";

// Reusable surface primitives for the RT app shell — page header, KPI stat tile,
// and section/right-rail panel. See docs SURFACE_PROMPTS for the pattern.

/** Page title + subtitle with an optional right-aligned actions cluster. */
export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="grid gap-1">
        <h1 className="font-display text-2xl font-black tracking-tight">{title}</h1>
        {subtitle && <p className="text-sm text-muted">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

type Tone = "accent" | "verified" | "info" | "warn" | "danger";
const toneCls: Record<Tone, string> = {
  accent: "bg-accent/15 text-accent-strong",
  verified: "bg-verified-bg text-verified",
  info: "bg-info/15 text-info",
  warn: "bg-warn-bg text-warn",
  danger: "bg-danger/15 text-danger",
};

/** KPI card: icon chip + label + big value + optional sub-line. Links when `to`. */
export function StatTile({
  icon, label, value, sub, tone = "accent", to,
}: {
  icon: string;
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: Tone;
  to?: string;
}) {
  const card = (extra = "") => (
    <div className={`flex items-center gap-3 rounded-card border border-line bg-surface p-4 ${extra}`}>
      <span className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl ${toneCls[tone]}`}>
        <Icon name={icon} className="h-5 w-5" />
      </span>
      <div className="grid gap-0.5">
        <span className="text-[11px] font-bold uppercase tracking-widest text-muted">{label}</span>
        <span className="font-display text-2xl font-black leading-none">{value}</span>
        {sub != null && <span className="font-mono text-xs text-muted">{sub}</span>}
      </div>
    </div>
  );
  return to ? <Link to={to} className="block">{card("transition-colors hover:border-accent")}</Link> : card();
}

/** Titled card used for main sections and right-rail panels. */
export function Panel({ title, action, children }: { title?: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="grid gap-3 rounded-card border border-line bg-surface p-4">
      {(title || action) && (
        <div className="flex items-center justify-between">
          {title && <h2 className="text-[11px] font-bold uppercase tracking-widest text-muted">{title}</h2>}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}
